import { useCallback, useState } from "react";
import { message } from "antd";

import { postRequest } from "helpers/utility/axiosServiceLayer";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import { ExportRequestBody, ExportResult } from "./types";
import { SelectedWafers } from "./ScanResultsBrowser";

const HOST_URL = process.env.REACT_APP_API_ENDPOINT!;

/**
 * Export one scan result's wafers to ADC and resolve to the folder it landed in.
 *
 * One request per scan result, carrying that scan result's wafers. Not one request per
 * wafer: the wafers of a scan result belong in a single output folder. Not one request
 * for everything either - DataServer names the folder and writes MetaData.json from the
 * first path it is given, so a batch spanning two scan results would label the whole
 * export after whichever came first. The backend rejects such a batch outright.
 *
 * Rejects with an Error whose message is the reason, ready to show as is.
 */
export async function exportScanResult(waferPaths: string[]): Promise<string> {
  let result: ExportResult;
  try {
    result = await postRequest<ExportResult, ExportRequestBody>(
      composeApiURL(HOST_URL, "/scan-results/export"),
      { paths: waferPaths },
    );
  } catch (e: any) {
    throw new Error(e.response?.data?.detail || "export request failed");
  }
  if (!result.success) throw new Error(result.error);
  // folder_name, not export_path: the latter is absolute on the DataServer host and
  // resolves to nothing inside the backend pod.
  if (!result.folder_name) {
    // The field is declared required, but this frontend and the backend ship separately,
    // so a response predating it is an ordinary thing to meet. Without a folder there is
    // nothing to hand the wizard, and pushing the undefined through crashed the page it
    // landed on.
    throw new Error(
      "exported, but the server returned no folder name - the backend may be out of date.",
    );
  }
  return result.folder_name;
}

export interface ExportOutcome {
  /** Datasets-relative folder names, ready to hand to the Add Data step. */
  exportedFolders: string[];
  /** One "<scan result>: <reason>" line per scan result that did not export. */
  failures: string[];
}

/**
 * Export a whole selection in one go, reporting progress and showing each failure as a
 * message. The standalone scan-results page's flow; the creation wizard queues exports
 * per scan result instead (see useScanExportQueue).
 */
export default function useScanResultsExport() {
  const [exportProgress, setExportProgress] = useState<string>("");

  const exportSelection = useCallback(
    async (selectedWafers: SelectedWafers): Promise<ExportOutcome> => {
      const groups = Object.entries(selectedWafers);
      const exportedFolders: string[] = [];
      const failures: string[] = [];

      // Sequential on purpose: each export copies every defect image on the DataServer
      // host, so running them at once would just contend. It also gives real per-scan
      // progress without any job-tracking machinery, since the call is synchronous.
      for (let i = 0; i < groups.length; i += 1) {
        const [srPath, waferPaths] = groups[i];
        setExportProgress(`Exporting ${i + 1} of ${groups.length}...`);
        try {
          exportedFolders.push(await exportScanResult(waferPaths));
        } catch (e: any) {
          failures.push(`${srPath}: ${e.message}`);
        }
      }
      setExportProgress("");

      // Listed individually rather than collapsed into a count: when the share is down
      // the reason matters far more than how many failed.
      failures.forEach((failure) => message.error(failure, 8));

      return { exportedFolders, failures };
    },
    [],
  );

  return { exportSelection, exportProgress };
}
